import React, { useEffect, useState, Suspense } from "react";
import { Form } from "react-bootstrap";
import { GridView, SvgComponent } from "components";
import usePaginationValuesWhere from '../../hooks/usePaginationValuesWhere';
import IconButton from "components/IconButton/IconButton";
import ToolTip from "components/ToolTip/ToolTip";
import ListHeader from "components/PantallaList/Header/ListHeader";
import Select from "react-select";
import { Tercero } from "models/ECF/Tercero";
import {
  RETENCIONES_ITBIS_CXC_OPTIONS,
  RETENCIONES_ISR_CXC_OPTIONS,
  RETENCIONES_ITBIS_CXP_OPTIONS,
  RETENCIONES_ISR_CXP_OPTIONS,
} from "models/ECF/Retenciones";
import { DEFAULT_PAGINATION_VALUES } from "utils";
import { TerceroManager } from "managers";
import Exportar from "components/Exportar/Exportar";
import SuspenseECF from "components/Suspense/SuspenseECF";
import useExceptionHandler from "hooks/useExceptionHandler";
import useMessage from "hooks/useMessage";
import { DateTimePicker } from "components/DateTimePicker/DateTimePicker";
import { DateHelper } from "utils";
import uuid from "react-uuid";
import TerceroEdit from "./TerceroEdit";


export default function TerceroList({ hideLoading, showLoading, isActive }) {
  const [where, setWhere] = useState({});
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION_VALUES);
  const [list, setList] = useState([]);
  const [idSec, setIdSec] = useState(undefined);
  const [showCanvas, setShowCanvas] = useState(false);
  const { setPaginationValuesWhere } = usePaginationValuesWhere();
  const [dateFilterkey, setDateFilterKey] = useState(uuid());
  const [fechaCreacionFilterkey, setFechaCreacionFilterKey] = useState(uuid());
  const { handleException } = useExceptionHandler();
  const [showExportar, setShowExportar] = useState(false);
  const { cerrarAlertas } = useMessage();

  const ESTATUS_LIST = [
    { value: "", label: "Todos" },
    ...(Tercero.ESTATUS_LIST || []),
  ];

  const TIPO_DOCUMENTO_LIST = [
    { value: "", label: "Todos" },
    ...(Tercero.TIPO_DOCUMENTO_LIST || []),
  ];

  const TIPO_COMPROBANTE_VENTA_LIST = [
    { value: "", label: "Todos" },
    ...(Tercero.TIPO_COMPROBANTE_VENTA_LIST || []),
  ];

  const TIPO_COMPROBANTE_COMPRA_LIST = [
    { value: "", label: "Todos" },
    ...(Tercero.TIPO_COMPROBANTE_COMPRA_LIST || []),
  ];

  const SI_NO_LIST = [
    { value: null,  label: "Todos" },
    { value: true,  label: "Sí" },
    { value: false, label: "No" },
  ];

  const RETENCIONES_ITBIS_CXC_LIST = [{ value: "", label: "Todos" }, ...RETENCIONES_ITBIS_CXC_OPTIONS];
  const RETENCIONES_ISR_CXC_LIST   = [{ value: "", label: "Todos" }, ...RETENCIONES_ISR_CXC_OPTIONS];
  const RETENCIONES_ITBIS_CXP_LIST = [{ value: "", label: "Todos" }, ...RETENCIONES_ITBIS_CXP_OPTIONS];
  const RETENCIONES_ISR_CXP_LIST   = [{ value: "", label: "Todos" }, ...RETENCIONES_ISR_CXP_OPTIONS];

  useEffect(() => {
    buscar();

    return () => {
      cerrarAlertas();
      hideLoading();
    };
  }, [where, isActive]);

  const updateList = () => {
    buscar();
  };

  const buscar = () => {
    const whereWithPaginationValues = {
      ...where,
      ...pagination,
      FechaCreacionInicio: where.RangoFechasCreacion?.fechaInicio,
      FechaCreacionFin: where.RangoFechasCreacion?.fechaFin,
    };
    TerceroManager.listado(whereWithPaginationValues)
      .then((res) => {
        setPaginationValuesWhere(res.Headers, pagination, setPagination);
        for (let tercero of res.Data) {
          tercero["FechaCreacion"] = DateHelper.formatStringDate(
            tercero["FechaCreacion"],
            true
          );
        }
        setList(res.Data);
      })
      .catch((error) => {
        handleException(error);
      })
      .finally(() => {
        hideLoading();
      });
  };

  const onPageIndexChanging = (pageNumber) => {
    setPagination((prevstate) => {
      return { ...prevstate, IndiceDePagina: pageNumber - 1 };
    });
    setWhere({ ...where });
  };

  const handleClick = (params) => {
    if (params && params.IdSec) {
      setIdSec(params.IdSec);
    } else {
      setIdSec(undefined);
    }
    setShowCanvas(true);
  };

  const openExportar = () => {
    setShowExportar(true);
  };

  return (
    <>
      <div style={{ paddingRight: "6px" }}>
        <ListHeader
          titulo="Terceros"
          crearNuevo={handleClick}
          exportar={openExportar}
        />
      </div>

      {showExportar && (
        <Suspense fallback={<SuspenseECF />}>
          <Exportar
            PROPIEDADES_EXPORTAR={Tercero.PROPIEDADES_EXPORTAR}
            showExportar={showExportar}
            setShowExportar={setShowExportar}
            showLoading={showLoading}
            hideLoading={hideLoading}
            where={where}
            serialize={true}
            exportarCallBack={TerceroManager.exportar}
          ></Exportar>
        </Suspense>
      )}

      {showCanvas && (
        <Suspense fallback={<SuspenseECF />}>
          <TerceroEdit
            showCanvas={showCanvas}
            setShowCanvas={setShowCanvas}
            IdSec={idSec}
            hideLoading={hideLoading}
            showLoading={showLoading}
            updateList={updateList}
          />
        </Suspense>
      )}

      <GridView
        pagination={pagination}
        setPagination={setPagination}
        allowPaging={pagination.EsPaginable}
        pageSize={pagination.CantidadPorPagina}
        pageIndex={pagination.IndiceDePagina}
        lastPageNumber={pagination.PaginasTotal}
        dataSource={list}
        onPageIndexChanging={onPageIndexChanging}
        where={where}
        setWhere={setWhere}
        onReset={() => {
          setDateFilterKey(uuid());
          setFechaCreacionFilterKey(uuid());
        }}
      >
        <GridView.Column
          isField
          width="100px"
          showClearFilter={true}
          bodyRenderer={(row) => {
            return (
              <div className="float-right">
                <IconButton onClick={() => handleClick({ IdSec: row.IdSec })}>
                  <ToolTip message="Editar">
                    <div>
                      <SvgComponent iconName="pencil" height="25" width="25" />
                    </div>
                  </ToolTip>
                </IconButton>
              </div>
            );
          }}
        />

        <GridView.Column
          headerText="Id"
          dataField="IdSec"
          sortExpression="IdSec"
          width="80px"
        />

        <GridView.Column
          headerText="Tipo Documento"
          dataField="TipoDocumentoDesc"
          sortExpression="TipoDocumentoDesc"
          width="150px"
          filterRenderer={() => {
            return (
              <Select
                className="react-select primary"
                options={TIPO_DOCUMENTO_LIST}
                value={TIPO_DOCUMENTO_LIST.filter(function (option) {
                  return (
                    option.value ===
                    (where.TipoDocumento ? where.TipoDocumento : TIPO_DOCUMENTO_LIST[0].value)
                  );
                })}
                name="TipoDocumento"
                onChange={(e) =>
                  setWhere({
                    ...where,
                    TipoDocumento: e.value,
                  })
                }
              />
            );
          }}
        />

        <GridView.Column
          headerText="Documento"
          dataField="Documento"
          sortExpression="Documento"
          width="200px"
        />

        <GridView.Column
          headerText="Razon Social"
          dataField="RazonSocial"
          sortExpression="RazonSocial"
          width="250px"
        />

        <GridView.Column
          headerText="Cliente"
          dataField="EsCliente"
          sortExpression="EsCliente"
          width="130px"
          bodyRenderer={(row) => <div style={{ textAlign: "center" }}>{row.EsCliente ? "Sí" : "No"}</div>}
          filterRenderer={() => (
            <Form.Check
              type="switch"
              id="filter-EsCliente"
              checked={where.EsCliente === true}
              onChange={(e) => setWhere({ ...where, EsCliente: e.target.checked ? true : null })}
            />
          )}
        />

        <GridView.Column
          headerText="Proveedor"
          dataField="EsProveedor"
          sortExpression="EsProveedor"
          width="130px"
          bodyRenderer={(row) => <div style={{ textAlign: "center" }}>{row.EsProveedor ? "Sí" : "No"}</div>}
          filterRenderer={() => (
            <Form.Check
              type="switch"
              id="filter-EsProveedor"
              checked={where.EsProveedor === true}
              onChange={(e) => setWhere({ ...where, EsProveedor: e.target.checked ? true : null })}
            />
          )}
        />

        <GridView.Column
          headerText="Estatus"
          dataField="EstatusDesc"
          sortExpression="EstatusDesc"
          width="150px"
          bodyRenderer={(row) => (
            <div style={{ textAlign: "center", verticalAlign: "middle" }}>
              {row.EstatusDesc}
            </div>
          )}
          filterRenderer={() => {
            return (
              <Select
                className="react-select primary"
                options={ESTATUS_LIST}
                value={ESTATUS_LIST.filter(function (option) {
                  return (
                    option.value ===
                    (where.Estatus ? where.Estatus : ESTATUS_LIST[0].value)
                  );
                })}
                name="Estatus"
                onChange={(e) =>
                  setWhere({
                    ...where,
                    Estatus: e.value,
                  })
                }
              />
            );
          }}
        />

        <GridView.Column
          headerText="Comprobante Venta"
          dataField="TipoComprobanteVentaDesc"
          sortExpression="TipoComprobanteVentaDesc"
          width="200px"
          filterRenderer={() => {
            return (
              <Select
                className="react-select primary"
                options={TIPO_COMPROBANTE_VENTA_LIST}
                value={TIPO_COMPROBANTE_VENTA_LIST.filter(function (option) {
                  return (
                    option.value ===
                    (where.TipoComprobanteVenta ? where.TipoComprobanteVenta : TIPO_COMPROBANTE_VENTA_LIST[0].value)
                  );
                })}
                name="TipoComprobanteVenta"
                onChange={(e) =>
                  setWhere({
                    ...where,
                    TipoComprobanteVenta: e.value,
                  })
                }
              />
            );
          }}
        />

        <GridView.Column
          headerText="Comprobante Compra"
          dataField="TipoComprobanteCompraDesc"
          sortExpression="TipoComprobanteCompraDesc"
          width="200px"
          filterRenderer={() => {
            return (
              <Select
                className="react-select primary"
                options={TIPO_COMPROBANTE_COMPRA_LIST}
                value={TIPO_COMPROBANTE_COMPRA_LIST.filter(function (option) {
                  return (
                    option.value ===
                    (where.TipoComprobanteCompra ? where.TipoComprobanteCompra : TIPO_COMPROBANTE_COMPRA_LIST[0].value)
                  );
                })}
                name="TipoComprobanteCompra"
                onChange={(e) =>
                  setWhere({
                    ...where,
                    TipoComprobanteCompra: e.value,
                  })
                }
              />
            );
          }}
        />

        <GridView.Column
          headerText="Telefono"
          dataField="Contacto"
          sortExpression="Contacto"
          width="180px"
        />

        <GridView.Column
          headerText="Correo"
          dataField="Correo"
          sortExpression="Correo"
          width="220px"
        />

        <GridView.Column
          headerText="Dirección"
          dataField="Direccion"
          sortExpression="Direccion"
          width="180px"
        />

        <GridView.Column
          headerText="Código"
          dataField="CodigoInterno"
          sortExpression="CodigoInterno"
          width="220px"
        />

        <GridView.Column
          headerText="Ret. ITBIS Venta"
          dataField="RetencionITBISVenta"
          sortExpression="RetencionITBISVenta"
          width="200px"
          bodyRenderer={(row) => {
            const opt = RETENCIONES_ITBIS_CXC_OPTIONS.find(o => o.value === row.RetencionITBISVenta);
            return opt ? opt.label : "";
          }}
          filterRenderer={() => (
            <Select
              className="react-select primary"
              options={RETENCIONES_ITBIS_CXC_LIST}
              value={RETENCIONES_ITBIS_CXC_LIST.find(o => o.value === (where.RetencionITBISVenta ?? ""))}
              name="RetencionITBISVenta"
              onChange={(e) => setWhere({ ...where, RetencionITBISVenta: e.value })}
            />
          )}
        />

        <GridView.Column
          headerText="Ret. ISR Venta"
          dataField="RetencionISRVenta"
          sortExpression="RetencionISRVenta"
          width="200px"
          bodyRenderer={(row) => {
            const opt = RETENCIONES_ISR_CXC_OPTIONS.find(o => o.value === row.RetencionISRVenta);
            return opt ? opt.label : "";
          }}
          filterRenderer={() => (
            <Select
              className="react-select primary"
              options={RETENCIONES_ISR_CXC_LIST}
              value={RETENCIONES_ISR_CXC_LIST.find(o => o.value === (where.RetencionISRVenta ?? ""))}
              name="RetencionISRVenta"
              onChange={(e) => setWhere({ ...where, RetencionISRVenta: e.value })}
            />
          )}
        />

        <GridView.Column
          headerText="No Aplica ITBIS Venta"
          dataField="NoAplicaITBISVenta"
          sortExpression="NoAplicaITBISVenta"
          width="180px"
          bodyRenderer={(row) => <div style={{ textAlign: "center" }}>{row.NoAplicaITBISVenta ? "Sí" : "No"}</div>}
          filterRenderer={() => (
            <Select
              className="react-select primary"
              options={SI_NO_LIST}
              value={SI_NO_LIST.find(o => o.value === (where.NoAplicaITBISVenta ?? null))}
              name="NoAplicaITBISVenta"
              onChange={(e) => setWhere({ ...where, NoAplicaITBISVenta: e.value })}
            />
          )}
        />

        <GridView.Column
          headerText="Ret. ITBIS Compra"
          dataField="RetencionITBISCompra"
          sortExpression="RetencionITBISCompra"
          width="200px"
          bodyRenderer={(row) => {
            const opt = RETENCIONES_ITBIS_CXP_OPTIONS.find(o => o.value === row.RetencionITBISCompra);
            return opt ? opt.label : "";
          }}
          filterRenderer={() => (
            <Select
              className="react-select primary"
              options={RETENCIONES_ITBIS_CXP_LIST}
              value={RETENCIONES_ITBIS_CXP_LIST.find(o => o.value === (where.RetencionITBISCompra ?? ""))}
              name="RetencionITBISCompra"
              onChange={(e) => setWhere({ ...where, RetencionITBISCompra: e.value })}
            />
          )}
        />

        <GridView.Column
          headerText="Ret. ISR Compra"
          dataField="RetencionISRCompra"
          sortExpression="RetencionISRCompra"
          width="200px"
          bodyRenderer={(row) => {
            const opt = RETENCIONES_ISR_CXP_OPTIONS.find(o => o.value === row.RetencionISRCompra);
            return opt ? opt.label : "";
          }}
          filterRenderer={() => (
            <Select
              className="react-select primary"
              options={RETENCIONES_ISR_CXP_LIST}
              value={RETENCIONES_ISR_CXP_LIST.find(o => o.value === (where.RetencionISRCompra ?? ""))}
              name="RetencionISRCompra"
              onChange={(e) => setWhere({ ...where, RetencionISRCompra: e.value })}
            />
          )}
        />

        <GridView.Column
          headerText="No Aplica ITBIS Compra"
          dataField="NoAplicaITBISCompra"
          sortExpression="NoAplicaITBISCompra"
          width="190px"
          bodyRenderer={(row) => <div style={{ textAlign: "center" }}>{row.NoAplicaITBISCompra ? "Sí" : "No"}</div>}
          filterRenderer={() => (
            <Select
              className="react-select primary"
              options={SI_NO_LIST}
              value={SI_NO_LIST.find(o => o.value === (where.NoAplicaITBISCompra ?? null))}
              name="NoAplicaITBISCompra"
              onChange={(e) => setWhere({ ...where, NoAplicaITBISCompra: e.value })}
            />
          )}
        />

        <GridView.Column
          date
          hideFilter
          headerText="Fecha Creacion"
          dataField="FechaCreacion"
          sortExpression="FechaCreacion"
          width="250px"
          filterRenderer={(onInputChange) => {
            return (
              <DateTimePicker
                key={fechaCreacionFilterkey}
                soloFecha
                esRangoFechas
                esGrid
                usarLabel={false}
                fechaInicioOriginal={where?.RangoFechasCreacion?.fechaInicio || ""}
                fechaFinOriginal={where?.RangoFechasCreacion?.fechaFin || ""}
                onChange={(e) =>
                  onInputChange({
                    target: { name: "RangoFechasCreacion", value: e },
                  })
                }
              />
            );
          }}
        />
      </GridView>
    </>
  );
}
